import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import Checkbox from '../../components/Checkbox';
import './SelectGitHubTestCasesModal.sass';

const SelectGitHubTestCasesModal = ({
  onClose = () => { },
  onSubmit = () => { },
  testCases = [],
  loading = false,
}) => {
  const [selected, setSelected] = useState([]);

  const normalizeTestCase = (tc) => {
    if (tc.description) {
      // Format 1: {file_name, group_name, method_name, description}
      return {
        id: `${tc.file_name}::${tc.group_name}::${tc.method_name}`,
        displayName: tc.description,
        details: `${tc.group_name} » ${tc.method_name}`,
      };
    } else if (tc.test_name) {
      // Format 2: {file_name, test_name, grep_name}
      return {
        id: `${tc.file_name}::${tc.test_name}`,
        displayName: tc.test_name,
        details: `Grep: ${tc.grep_name || 'N/A'} » File: ${tc.file_name}`,
      };
    }
    // Fallback for unknown format
    return {
      id: JSON.stringify(tc),
      displayName: 'Unknown test case format',
      details: '',
    };
  };

  useEffect(() => {
    // Pre-select only non-duplicate test cases by default, excluding suite items
    const initial = (testCases || []).filter((tc) => !tc._alreadyImported && tc.entity_type !== 'suite');
    setSelected(initial);
  }, [testCases]);

  const handleToggle = (testCase) => {
    if (testCase._alreadyImported) return; // ignore clicks on disabled items
    setSelected(prevSelected => {
      const isSelected = prevSelected.some(item => JSON.stringify(item) === JSON.stringify(testCase));
      if (isSelected) {
        return prevSelected.filter(item => JSON.stringify(item) !== JSON.stringify(testCase));
      } else {
        return [...prevSelected, testCase];
      }
    });
  };

  const handleToggleAll = () => {
    const selectable = (testCases || []).filter((tc) => !tc._alreadyImported);
    if (selected.length === selectable.length) {
      setSelected([]);
    } else {
      setSelected(selectable);
    }
  };

  const handleSubmit = () => {
    onSubmit(selected);
  };

  const totalSelectable = (testCases || []).filter(tc => !tc._alreadyImported).length;
  const isAllSelected = selected.length === totalSelectable && totalSelectable > 0;

  return (
    <Modal
      title="Select Test Cases to Import"
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      submitButtonText={`Import ${selected.length} Test Case(s)`}
      submitEnabled={selected.length > 0 && !loading}
      size="lg" // Make modal larger
    >
      <div className="select-test-cases-modal">
        <p>The following test cases were found. Select the ones you want to import.</p>

        <div className="select-all-toggle">
          <Checkbox
            id="select-all"
            checked={isAllSelected}
            onChange={handleToggleAll}
            disabled={totalSelectable === 0}
          />
          <label htmlFor="select-all">
            Select All {totalSelectable > 0 ? `(${totalSelectable} available)` : '(0 available)'}
          </label>
        </div>

        <ul className="test-case-list">
          {testCases.map((tc, index) => {
            const normalized = normalizeTestCase(tc);
            const isChecked = selected.some(item => JSON.stringify(item) === JSON.stringify(tc));
            const isDisabled = !!tc._alreadyImported;
            const isSuite = tc.entity_type === 'suite';
            return (
              <li key={`${normalized.id}-${index}`} className={`test-case-item ${isDisabled ? 'disabled-item' : ''} ${isSuite ? 'suite-item' : ''}`}>
                <Checkbox
                  id={`${normalized.id}-${index}`}
                  checked={isChecked}
                  onChange={() => handleToggle(tc)}
                  disabled={isDisabled}
                />
                <label htmlFor={`${normalized.id}-${index}`} className="test-case-description">
                  <div className="test-name-line">
                    {isSuite && <i className="bi bi-collection suite-icon" title="Test Suite"></i>}
                    <span className="test-name">{normalized.displayName}</span>
                    {isSuite && (
                      <span className="badge-suite" title="Test Suite - Runs all tests in this file">
                        <i className="bi bi-play-circle-fill"></i> Suite
                      </span>
                    )}
                    {isDisabled && (
                      <span className="badge-disabled" title="This test case has already been imported">
                        <i className="bi bi-check-circle"></i> Imported
                      </span>
                    )}
                  </div>
                  <small className="test-case-details">
                    {normalized.details}
                  </small>
                  {isSuite && (
                    <small className="suite-info-message">
                      <i className="bi bi-info-circle"></i> This is test.describe job, selecting this will trigger all test case execution for this file
                    </small>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
};

export default SelectGitHubTestCasesModal; 